import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail } from "lucide-react";
import { api } from "../../lib/api";

const Count = ({ label, n, tone }) => (
  <div className={`rounded-lg border p-3 ${tone}`} data-testid={`report-count-${label.toLowerCase()}`}><p className="text-2xl font-bold tabular-nums">{n}</p><p className="text-xs">{label}</p></div>
);

export const ImportReportStep = ({ report, onRestart }) => {
  const navigate = useNavigate();
  const { counts, report: r, session_id } = report;
  const download = async () => {
    try {
      const res = await api.get(`/directory-import/sessions/${session_id}/failed.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `import_${session_id}_failed_rows.csv`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="space-y-5" data-testid="import-report-step">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Count label="Added" n={counts.added} tone="bg-emerald-50 border-emerald-200 text-emerald-900" />
        <Count label="Updated" n={counts.updated} tone="bg-sky-50 border-sky-200 text-sky-900" />
        <Count label="Unchanged" n={counts.unchanged} tone="bg-slate-50 border-slate-200 text-slate-800" />
        <Count label="Skipped" n={counts.skipped} tone="bg-amber-50 border-amber-200 text-amber-900" />
        <Count label="Failed" n={counts.failed} tone="bg-rose-50 border-rose-200 text-rose-900" />
      </div>
      {(r.failed.length > 0 || r.skipped.length > 0) && (
        <div className="nv-card p-5 space-y-2" data-testid="report-problems">
          <div className="flex items-center justify-between">
            <h3 className="text-h3">Rows not imported</h3>
            <button onClick={download} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-200" data-testid="report-download-failed"><Download size={13} /> Download for correction (CSV)</button>
          </div>
          <ul className="text-xs space-y-1">
            {r.failed.map((f) => <li key={`f${f.row}`} className="text-rose-700" data-testid={`report-failed-${f.row}`}>Row {f.row} · {f.email || "no email"} — <b>failed:</b> {f.reason}</li>)}
            {r.skipped.map((f) => <li key={`s${f.row}`} className="text-amber-800" data-testid={`report-skipped-${f.row}`}>Row {f.row} · {f.email || "no email"} — skipped: {f.reason}</li>)}
          </ul>
        </div>
      )}
      {r.added.length > 0 && (
        <div className="nv-card p-5 space-y-2" data-testid="report-added">
          <h3 className="text-h3">Added accounts</h3>
          <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Mail size={13} /> No invitations were sent. Use <b>Reset password</b> on each user in User Management when you are ready to invite them.</p>
          <ul className="text-xs grid grid-cols-1 md:grid-cols-2 gap-1">{r.added.map((a) => <li key={a.user_id}>{a.email} · {a.access_role} · {a.homes.join(", ") || "no home"}{a.staff_record ? " · staff record linked" : ""}</li>)}</ul>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => navigate("/users")} className="px-4 py-2 text-sm rounded-md bg-sky-700 text-white" data-testid="report-open-directory">Open User Management</button>
        <button onClick={onRestart} className="px-4 py-2 text-sm rounded-md border border-slate-200" data-testid="report-import-another">Import another file</button>
      </div>
    </div>
  );
};
